/**
 * Chat message shaping helpers — ported from mobile's
 * `Screens/chat/chat-helper/index.js` (transformToGiftedChatObjects) and
 * the `handleMessageUpdate` merge logic in `Screens/chat/index.js`, adapted
 * for this panel's plain-object message model instead of GiftedChat's.
 *
 * Chat-window open/closed logic and the current-user-id resolver already
 * exist in eventPermissions.js (getChatStatus / resolveCurrentUserId) —
 * reuse those rather than duplicating them here.
 *
 * Field names below mirror mobile's SignalR payload exactly (camelCase —
 * the hub's JSON contract differs from this app's PascalCase REST
 * responses). Per this project's porting-workflow lesson, treat this as
 * unverified against the real hub until confirmed with a live send/receive
 * round-trip.
 */

import leoProfanity from "leo-profanity";
import { extraProfanityWords } from "./profanityWords";

const EMAIL_REGEX = /\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/i;
const URL_REGEX = /(https?:\/\/|www\.|t\.me\/|wa\.me\/|bit\.ly\/|tinyurl\.com\/|instagram\.com\/|facebook\.com\/|x\.com\/|twitter\.com\/|youtu\.be\/|youtube\.com\/)/i;
const DATE_REGEX = /\b\d{1,2}[.\-/]\d{1,2}[.\-/]\d{2,4}\b/;
const INTERNATIONAL_PHONE_REGEX = /^\+?[1-9]\d{1,14}$/;

let profanityConfigured = false;
const configureProfanityFilter = () => {
  if (profanityConfigured) return;
  try {
    leoProfanity.clearList();
    const en = leoProfanity.getDictionary("en") || [];
    const de = leoProfanity.getDictionary("de") || [];
    leoProfanity.add([...new Set([...en, ...de])]);
    extraProfanityWords.forEach((word) => leoProfanity.add(word.toLowerCase()));
    profanityConfigured = true;
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn("[chat] profanity dictionary load skipped:", err);
  }
};

/**
 * Pre-send message validation — ported from mobile's chat/index.js
 * `validateMessage`. Blocks email addresses, links, bare phone numbers, and
 * profanity (leo-profanity's en/de dictionaries + extraProfanityWords,
 * covering English/German/Urdu). Date-like text ("12.08.2026") is
 * deliberately allowed even though it can structurally resemble a blocked
 * pattern — checked, and short-circuited to "allowed", before the phone
 * check, same order as mobile.
 *
 * Returns `null` when the message is fine to send, or an i18n key under
 * `composer.validation.*` (see chat.json) naming why it was blocked.
 */
export const validateMessageText = (text) => {
  if (!text) return null;

  try {
    configureProfanityFilter();
    const lower = text.toLowerCase();

    if (EMAIL_REGEX.test(lower)) return "composer.validation.email";
    if (URL_REGEX.test(lower)) return "composer.validation.link";
    if (DATE_REGEX.test(lower)) return null; // dates allowed — see comment above
    if (INTERNATIONAL_PHONE_REGEX.test(lower)) return "composer.validation.phone";
    // leo-profanity tokenizes on whitespace only, so "bitch!"/"asshole." never
    // match their dictionary entries even though the bare word does — strip
    // punctuation before checking (profanity check only; the regexes above
    // still run against the untouched text).
    if (leoProfanity.check(lower.replace(/[^\p{L}\p{N}\s]/gu, " "))) return "composer.validation.profanity";

    return null;
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn("[chat] message validation error:", err);
    return null; // fail open, matches mobile
  }
};

/**
 * MessageDelivered/MessageSeen fire once per recipient with a single
 * record, not the array shape history entries carry — confirmed live
 * (`{messageId, deliveredTo: {...one user...}, deliveredToAll}`). Normalize
 * either shape to an array before merging.
 */
const asArray = (value) => (value == null ? [] : Array.isArray(value) ? value : [value]);

/**
 * Best-effort read of a user record's id/display name/avatar. Confirmed
 * live from `UserOnline`: `{userId, displayName}` — this backend's user
 * shape, not mobile's assumed `{id, name}`. Checked first; older guesses
 * kept as fallbacks in case a different event uses a different shape.
 */
export const getUserId = (u = {}) => u.userId ?? u.UserId ?? u.id ?? u.Id;
export const getUserName = (u = {}) => u.displayName ?? u.DisplayName ?? u.name ?? u.Name ?? u.nickName ?? u.NickName;
export const getUserAvatar = (u = {}) => u.displayPicture ?? u.DisplayPicture ?? u.avatar ?? u.Avatar;

/**
 * Non-text message content ("customMessage" in mobile's transformToGiftedChatObjects)
 * is either a bare URL (image messages — the whole field IS the URL) or a
 * `key=value;key=value` string (video/pdf — e.g. "video_url=...;video_thumbnail=...",
 * "file_url=...;name=...;size=..."), ported verbatim from mobile's
 * chat-helper and bubble-chat parsing so the Panel decodes the exact same
 * payload shape the app sends.
 */
export const parseCustomMessage = (text) => {
  if (!text || !text.includes("=")) return {};
  return String(text)
    .split(";")
    .reduce((acc, pair) => {
      const [key, value] = pair.split("=");
      if (key) acc[key.trim()] = value;
      return acc;
    }, {});
};

/** Raw SignalR/history message -> normalized shape used throughout the chat UI. */
export const normalizeMessage = (raw = {}) => {
  const sender = raw.sender || raw.Sender || {};
  const id = raw.messageId ?? raw.MessageId ?? raw.id ?? raw.Id;
  const text = raw.editedMessage ?? raw.newMessage ?? raw.text ?? raw.message ?? raw.Message ?? "";

  return {
    id,
    text,
    messageType: raw.messageType || raw.MessageType || "text",
    createdAt: raw.timestamp || raw.createdAt || raw.Timestamp || new Date().toISOString(),
    senderId: getUserId(sender) ?? raw.userId ?? raw.UserId,
    senderName: getUserName(sender),
    senderAvatar: getUserAvatar(sender),
    senderUserType: sender.userType ?? sender.UserType,
    isEdited: !!(raw.isEdit || raw.isEdited || raw.IsEdited),
    deliveredTo: asArray(raw.deliveredTo || raw.DeliveredTo),
    seenBy: asArray(raw.seenBy || raw.SeenBy),
    deliveredToAll: !!(raw.deliveredToAll ?? raw.DeliveredToAll),
    seenByAll: !!(raw.seenByAll ?? raw.SeenByAll),
    // Absent on every message this panel could see before the media-approval
    // gate existed, and on any room that hasn't opted in — "Approved" is the
    // correct default for both. Only ever "Pending" on a live push, and only
    // ever to the sender's own connection (ChatHub.SendMessage never
    // broadcasts a Pending/Rejected message to anyone else, and
    // GetChatHistory/GetChatHistoryAsync already filter them out room-wide).
    approvalStatus: raw.approvalStatus || raw.ApprovalStatus || "Approved",
    rejectionReason: raw.rejectionReason ?? raw.RejectionReason ?? null,
  };
};

const uniqById = (list) => {
  const seen = new Set();
  const out = [];
  for (const item of list) {
    const key = getUserId(item) ?? JSON.stringify(item);
    if (key == null || seen.has(key)) continue;
    seen.add(key);
    out.push(item);
  }
  return out;
};

/**
 * Merges an incremental update (MessageEdited/MessageDelivered/MessageSeen
 * payload) into an already-normalized message. `deliveredTo`/`seenBy`
 * arrays are merged (not replaced), mirroring mobile's lodash `uniqBy`
 * behavior, since these events report incrementally per-recipient.
 */
export const mergeMessageUpdate = (existing, raw = {}) => {
  const next = { ...existing };

  // Confirmed live from MessageEdited: {id, messageId, newMessage, isEdit}
  // — "newMessage" is the real field, not "editedMessage"/"text"/"message".
  const editedText = raw.newMessage ?? raw.NewMessage ?? raw.editedMessage ?? raw.EditedMessage ?? raw.text ?? raw.Text ?? raw.message ?? raw.Message;
  if (editedText != null) next.text = editedText;
  if (editedText != null || raw.isEdit || raw.isEdited || raw.IsEdited) next.isEdited = true;

  const deliveredTo = raw.deliveredTo || raw.DeliveredTo;
  if (deliveredTo) next.deliveredTo = uniqById([...(existing.deliveredTo || []), ...asArray(deliveredTo)]);

  const seenBy = raw.seenBy || raw.SeenBy;
  if (seenBy) next.seenBy = uniqById([...(existing.seenBy || []), ...asArray(seenBy)]);

  if (typeof (raw.deliveredToAll ?? raw.DeliveredToAll) === "boolean") next.deliveredToAll = raw.deliveredToAll ?? raw.DeliveredToAll;
  if (typeof (raw.seenByAll ?? raw.SeenByAll) === "boolean") next.seenByAll = raw.seenByAll ?? raw.SeenByAll;

  // Not a real hub event shape — ChatContext synthesizes this same
  // {approvalStatus, rejectionReason} raw shape locally when a moderator
  // rejects a message the sender already has rendered as Pending.
  const approvalStatus = raw.approvalStatus || raw.ApprovalStatus;
  if (approvalStatus) next.approvalStatus = approvalStatus;
  if (raw.rejectionReason ?? raw.RejectionReason) next.rejectionReason = raw.rejectionReason ?? raw.RejectionReason;

  return next;
};

/** Short role-badge label for a chat participant, mirroring mobile's Avatar EA/SP/P/H badges. Best-effort — exact UserType enum values are unverified against the real API. */
export const getUserTypeBadge = (userType) => {
  switch (userType) {
    case "BusinessAssistant":
      return "BA";
    case "SubPromoter":
      return "SP";
    case "Promoter":
      return "P";
    default:
      return "H";
  }
};

/** One-line preview for an inbox row, mirroring RoomList.jsx's inline last-message rendering. */
export const formatLastMessagePreview = (lastMessage, t) => {
  if (!lastMessage) return "";
  switch (lastMessage.MessageType || lastMessage.messageType) {
    case "image":
      return t("inbox.previewPhoto");
    case "video":
      return t("inbox.previewVideo");
    case "pdf":
      return t("inbox.previewFile");
    case "push_button":
      return t("inbox.previewUpdate");
    default:
      return lastMessage.Message || lastMessage.message || "";
  }
};
